// @ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { cloneElement, type FC, type ReactElement } from 'react';
import cn from 'classnames';

import { _isNotEmpty } from '@/littledash';

import type { MetadataField } from '@/model/Metadata.model';

import { matchType, Type } from '@/referenceData/study/type';

import { MetadataCondition } from '@/components/Glossary/Sections/Approvals/TemplateForm/MetadataCondition';
import { studyTypeOptions } from '@/components/Glossary/Sections/Approvals/TemplateForm/TemplateForm.utils';
import { Radio } from '@/components/UI/FormFields';
import DraggableList, { asListItem } from '@/components/UI/List/DraggableList';

import { ErrorMessage } from '@hookform/error-message';

interface ReviewTemplateItem {
  id: number | string;
  name: string;
}

interface ApprovalLike {
  id?: number | string;
  name?: string;
  study_type?: string;
  match_type?: string;
  conditions?: Array<Record<string, unknown>>;
  dependencies?: string | number | '';
}

interface GeneralFormSubmitPayload {
  name: string;
  study_type: string;
  match_type: string;
  conditions: Array<Record<string, unknown>>;
  dependencies: string | number | '';
}

interface GeneralProps {
  ActionButtons: ReactElement<{ onOk?: () => void }>;
  onSubmit: (data: GeneralFormSubmitPayload) => void;
  approval: ApprovalLike;
  studyMetadataOptions: Array<MetadataField>;
  projectMetadataOptions: Array<MetadataField>;
  reviewTemplates: Array<ReviewTemplateItem>;
}

const General: FC<GeneralProps> = ({
  ActionButtons,
  onSubmit,
  approval,
  studyMetadataOptions,
  projectMetadataOptions,
  reviewTemplates,
}) => {
  const formMethods = useForm({
    defaultValues: {
      name: approval.name ?? '',
      study_type: approval.study_type ?? Type.all,
      match_type: approval.match_type ?? matchType.any,
      conditions: approval.conditions ?? [],
      dependencies: approval.dependencies ?? '',
    },
  });

  const { handleSubmit, register, errors, control } = formMethods;

  const {
    fields: conditions,
    append: appendToConditions,
    remove: removeFromConditions,
  } = useFieldArray({
    control,
    name: 'conditions',
  });

  const handleAddConditionClick = () => {
    appendToConditions({});
  };

  const handleRemoveConditionClick = (index: number) => {
    removeFromConditions(index);
  };

  const removeExistingTemplate = (): Array<ReviewTemplateItem> => {
    return reviewTemplates.filter((template) => Number(approval.id) !== Number(template.id));
  };

  const onSubmitClick = (data: GeneralFormSubmitPayload) => {
    const approvalData: GeneralFormSubmitPayload = {
      ...data,
      conditions: data.conditions ?? [],
    };
    onSubmit(approvalData);
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmitClick)}
        className="ui-card pa3"
        data-test-component="General"
        data-test-element="form"
      >
        <div className="mb3">
          <label htmlFor="general-review-name">Review name</label>
          <input
            id="general-review-name"
            type="text"
            data-test-element="name-input"
            style={{ marginBottom: 0 }}
            className={cn({ input__error: Boolean(errors.name) })}
            name="name"
            ref={register({
              required: 'Review name is required',
            })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>

        <fieldset className="bn pa0 ma0 mb3">
          <legend className="dib mb3">Review applies to:</legend>
          {studyTypeOptions.map((t) => (
            <div className="flex items-center pb2" key={t.value}>
              <input
                id={`study_type.${t.value}`}
                type="radio"
                data-test-element="type-radio"
                data-test-key={t.value}
                name="study_type"
                ref={register()}
                value={t.value}
                className="pointer ui__form__radio"
                style={{ marginBottom: 0 }}
              />
              <label className="pointer pl2" style={{ marginBottom: 0 }} htmlFor={`study_type.${t.value}`}>
                {t.name}
              </label>
            </div>
          ))}
        </fieldset>

        {_isNotEmpty(reviewTemplates) && (
          <div className="mb3">
            <label htmlFor="general-dependencies">Review dependency</label>
            <p className="lt-copy f6 pb2">
              This review can&apos;t be submitted until the selected review template has been completed
            </p>
            <select
              id="general-dependencies"
              name="dependencies"
              ref={register({ valueAsNumber: true })}
              style={{ width: 300 }}
            >
              <option value="">No dependency</option>
              {removeExistingTemplate().map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="pb3">
          <h3 className="lh-title normal f6">Metadata conditions</h3>
          <p className="lh-copy f6 ">
            Trigger reviews based on text and select metadata values entered during study creation.
          </p>
        </div>

        {conditions.length > 1 && (
          <div className="pb3">
            <div className="flex flex-wrap">
              <Radio
                options={[
                  {
                    value: matchType.all,
                    name: 'all',
                    label: 'All conditions',
                  },
                  {
                    value: matchType.any,
                    name: 'any',
                    label: 'Any condition',
                  },
                ]}
                label="Studies must match"
                name="match_type"
              />
            </div>
          </div>
        )}

        <DraggableList
          canAmend
          canAdd
          canRemove
          data={conditions}
          renderItem={asListItem(MetadataCondition, {
            studyMetadataOptions,
            projectMetadataOptions,
          })}
          onAddClick={handleAddConditionClick}
          onRemoveClick={handleRemoveConditionClick}
          addButtonText="Add condition"
          isRemoveButtonFluid={false}
        />

        {cloneElement(ActionButtons, { onOk: handleSubmit(onSubmitClick) })}
      </form>
    </FormProvider>
  );
};

export default General;
